import { useEffect } from 'react';
import { DeviceOSType } from '@/api/user-portal-api-generated';
import {
  attemptAppLaunch,
  getAndroidPackage,
  getInjectedIosAppStoreId,
  getIosAppStoreId,
  getMobilePlatform,
} from '@/features/onboarding/utils/mobile-platform';

const PLAY_STORE_BASE_URL = 'https://play.google.com/store/apps/details';
const APP_STORE_BASE_URL = 'https://apps.apple.com/app';

interface UseMobileInstallAppProps {
  deepLinkUrl: string;
}

export function useMobileInstallApp({ deepLinkUrl }: UseMobileInstallAppProps) {
  const mobilePlatform = getMobilePlatform();
  const androidPackage = getAndroidPackage();
  const iosAppStoreId = getIosAppStoreId();

  const playStoreUrl = `${PLAY_STORE_BASE_URL}?id=${androidPackage}`;
  const appStoreUrl = `${APP_STORE_BASE_URL}/id${iosAppStoreId}`;

  const showPlayStore =
    mobilePlatform === DeviceOSType.ANDROID || mobilePlatform === null;
  const showAppStore =
    mobilePlatform === DeviceOSType.IOS || mobilePlatform === null;

  useEffect(() => {
    attemptAppLaunch(deepLinkUrl);
  }, [deepLinkUrl]);

  useIosSmartAppBanner({
    mobilePlatform,
    iosAppStoreId: getInjectedIosAppStoreId(),
    deepLinkUrl,
  });

  const handleOpenApp = () => {
    window.location.href = deepLinkUrl;
  };

  return {
    playStoreUrl,
    appStoreUrl,
    showPlayStore,
    showAppStore,
    handleOpenApp,
  };
}

interface UseIosSmartAppBannerProps {
  mobilePlatform: DeviceOSType | null;
  iosAppStoreId: string | null;
  deepLinkUrl: string;
}

function useIosSmartAppBanner({
  mobilePlatform,
  iosAppStoreId,
  deepLinkUrl,
}: UseIosSmartAppBannerProps) {
  useEffect(() => {
    if (mobilePlatform !== DeviceOSType.IOS || !iosAppStoreId) {
      return;
    }

    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=${iosAppStoreId}, app-argument=${deepLinkUrl}`;
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, [mobilePlatform, iosAppStoreId, deepLinkUrl]);
}
